import { useState, useEffect, useContext, useRef, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useInView } from 'react-intersection-observer';

import { addToBag, getCrossSelling, getProduct, getCart, retrieveBag, getRokuStock } from 'actions/products';
import { debugAPI } from 'actions/general';

import { useIsMounted } from 'hooks';
import { useIsIpad } from 'hooks/useIsIpad';

import { StarRate } from 'components/ui/star-rate';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { DialogRoot, Dialog, DialogHeader } from 'components/ui/dialog';
import { ProductInfoSection } from 'components/productInfoSection';
import { TargetLinks, GiftCardTargetLinks } from 'components/productPurchaseCol/targetLinks';
import { ProductBrands } from 'components/productPurchaseCol/product-brands';
import { ProductBreadcrumbs } from 'components/productPurchaseCol/productBreadcrumbs';

import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { useSunnei } from 'context/product-sheet';
import { handleAddToBag } from 'context/bag';

import { setCookie, obtainVoucherPrices, getCookie, COOKIES, eraseCookie, getLang, getMarket, handleStorage, getFixedPrice } from 'utils/helpers';
import { getCountryFromMarket } from 'utils/translations';
import { dataLayerCartClick, dataLayerCartClickV2, dataLayerHandleEvent } from 'utils/dataLayers';
import { MAX_BAG_LENGTH, FAMILIES, FIXED_BLOCKED_SKUS } from 'utils/constants/products';
import { BAZAAR_VOICE_MARKETS } from 'utils/constants/ux';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { SIZE_GUIDE_WITH_MODELS } from 'utils/constants/sizes';
import { setBagCookies } from 'utils/bag';
import { getDomainValue } from 'utils/domain';

import styles from './style.module.css';
import { ProgressiveImg } from 'components/base/image';

const SizesList: any = dynamic(() => import('components/sizesList').then((mod) => mod.SizesList) as any);
const ONESVG = dynamic(() => import('components/iconsSVG/one').then((mod) => mod.ONESVG));
const RateSVG = dynamic(() => import('components/iconsSVG/rate').then((mod) => mod.RateSVG));
const SizesGuide: any = dynamic(() => import('components/sizesGuide').then((mod) => mod.default) as any);
const GiftCard: any = dynamic(() => import('components/giftCard').then((mod) => mod.default) as any);
const NotifyMeButton = dynamic(() => import('components/notifyMeButton'));
const AddToBagBtn: any = dynamic(() => import('./addToBagBtn').then((mod) => mod.AddToBagBtn) as any);
// const AddToWishlistBtn = dynamic(() => import('./addToWishlistBtn').then((mod) => mod.default));
const AfterpayButton = dynamic(() => import('components/checkout/afterpayButton').then((mod) => mod.default));
const KboixMidsoles = dynamic(() => import('components/pdp/midsoles').then((mod) => mod.KboixMidsoles));
const DispoModal = dynamic(() => import('components/dispoModal').then((mod) => mod.DispoModalWithButton));

type FailedItemProps = {
  image: string;
  name: string;
  sku: string;
};
function FailedItem({ image, name, sku }: FailedItemProps) {
  return (
    <div className="flex h-full w-full max-w-32 flex-grow select-none flex-col gap-2 overflow-hidden xl:max-w-40">
      {/* Image */}
      <div className="block w-full">
        <ProgressiveImg
          src={image}
          pictureClassName="block  aspect-square lab:aspect-auto w-full"
          className="aspect-square w-full object-cover lab:aspect-auto"
        />
      </div>
      {/* Name */}
      <div className="mt-auto flex flex-grow flex-col gap-1.5">
        <div>
          <p className="mb-0 text-sm nnormal:font-secondary nnormal:font-bold lab:text-xs lab:font-bold lab:uppercase">{name}</p>
          {/* Description */}
          <p className="mb-0 text-xs text-disabled empty:hidden lab:text-xs lab:font-bold lab:uppercase">{sku}</p>
        </div>
      </div>
    </div>
  );
}
// Unified failure modal state for multi-add flows (ROKU, SUNNEI)
type FailureSource = 'roku' | 'sunnei';
type FailedItemNormalized = { id: string; name: string; image: string };
type FailureModalState = { isOpen: boolean; source: FailureSource | null; items: FailedItemNormalized[] };

export function ProductPurchaseCol({
  productSheet,
  averageRate,
  hasReviews,
  locale,
  market,
  isCamperOne = false,
  isGiftCard = false,
  isSock = false,
  onClickReviews = () => {},
  isFullScreen = false,
  recalculating = false,
  voucherApplied = false,
  voucherPriceText = null,
  isRoku = false,
  isDimReady = false,
  usrhashValue = null,
}: TODO) {
  const {
    t,
    bagLength,
    profileData,
    setBagLength,
    bagHasMembersOnlyProduct,
    bag,
    setBag,
    recommendedProductsForBag,
    setRecommendedProductsForBag,
    setPromoLabel,
    setPromoLabelType,
    storeData,
    disabledProducts,
  } = useI18n();
  const isMounted = useIsMounted();

  const { userData, increaseShouldRecalculateVouchers, contextFilters, activatedPrevouchers }: any = useUser();
  const { hasSunneiConfig, selectedSunneiShoes, sunneiInfo, setSelectedSize: setSunneiSize, firstProductHasStock, secondProductHasStock } = useSunnei();
  const { config } = profileData;
  const showFreeShipping = config?.show_free_shipping || false;
  const showGiftPurchase = config?.show_gift_purchase || false;
  const { ref, inView } = useInView({ threshold: 1 });
  const availablePrice = productSheet.prices && productSheet.prices?.current && productSheet?.notifyMeType !== 'OUT';
  const { isAccessory, sizes, code, prices, camperlab, notifyMe } = productSheet;
  const isAvailableAccesory = isAccessory && availablePrice && sizes && sizes.length > 0 ? sizes.filter((size: any) => size.available).length > 0 : false;
  const isMembersOnly = productSheet?.membersOnly || false;
  const isUniqueSize = sizes.length === 1;
  const defaultSelectedSizeValue = (isAccessory && isAvailableAccesory) || isUniqueSize ? (sizes[0]?.value ?? ' ') : undefined;
  const hasMidsoles = Array.isArray(productSheet.kboixData.midsoles) && productSheet.kboixData.midsoles.length > 0;

  const [isMouseOverCartBtn, setIsMouseOverCartBtn] = useState(false);
  const [cartError, setCartError] = useState<string | boolean>(false);
  const [selectedSize, setSelectedSize] = useState(isAccessory && isAvailableAccesory ? (sizes[0]?.value ?? ' ') : undefined);
  const [loading, setLoading] = useState(false);

  const [failureModal, setFailureModal] = useState<FailureModalState>({ isOpen: false, source: null, items: [] });
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [defaultSelectedSize, setDefaultSelectedSize] = useState(defaultSelectedSizeValue);

  const reviewsCount = hasReviews ? productSheet.reviewStatistics?.TotalReviewCount : 0;
  const country = getCountryFromMarket(market);
  const limitedSizes = sizes && sizes.filter((size: any) => size.quantity === 1).map((item: any) => item.value);
  const selectedSizeItem = sizes && sizes.filter((size: any) => size?.value === selectedSize);
  const selectedSizePrice = selectedSizeItem.length && selectedSizeItem[0].price?.current;
  const totalPrice = prices !== null ? selectedSizePrice || prices?.current || prices?.previous : null;
  const availableSizes = sizes.filter((item: any) => item.available).map((size: any) => size.value);
  const isBuyingDisabled = disabledProducts === code || FIXED_BLOCKED_SKUS.includes(code);
  const [skusWithoutStock, setSkusWithoutStock] = useState<any[] | null>(null);
  const [selectedMidsoles, setSelectedMidsoles] = useState(() => {
    return productSheet.kboixData?.midsoles?.filter((sole: any) => sole.selected && sole.sizes.some((s: any) => s.available)) ?? [];
  });
  const appmode = useIsIpad();
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  // si seteamos directamente el showSizeModal a true dependiendo de las variables da un error de hidratación
  useEffect(() => {
    if ((isRoku || hasSunneiConfig) && !selectedSize) {
      setShowSizeModal(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoku, hasSunneiConfig]);

  const handleGetRokuStock = async (size: any) => {
    const partsWithoutStock = await getRokuStock({ context: undefined, profile: locale, size });

    if (partsWithoutStock) {
      // request OK
      if (Array.isArray(partsWithoutStock)) {
        setSkusWithoutStock(partsWithoutStock);
      }
    }
  };

  const handleSelectSize = async (size: any) => {
    setSelectedSize(size);
    setSunneiSize(size);

    // hay que controlar que las midsoles no esten seleccionadas si no tienen stock;
    setSelectedMidsoles((prev: any[]) => {
      return prev.filter((sole: any) => sole.sizes.find((item: any) => item.value === size)?.available === true);
    });

    // puesto que productSheet no tiene control sobre selectedSize
    // necesito hacer esto para leer selectedSize mientras calculo vouchers
    // así puedo cambiar los perSize una vez se selecciona la talla
    window.selectedSizeForVoucher = size;
    increaseShouldRecalculateVouchers();

    setCartError(false);

    dataLayerHandleEvent({ event: 'item_size_selection', event_category: 'product detail', event_detail_1: 'size selection', event_detail_2: size });

    if (isRoku) {
      await handleGetRokuStock(size);
    }
  };

  // Helpers for unified failure handling
  const openFailureModal = ({ source, items }: { source: FailureSource; items: FailedItemNormalized[] }): void => {
    setFailureModal({ isOpen: true, source, items });
  };

  const closeFailureModal = (): void => {
    setFailureModal({ isOpen: false, source: null, items: [] });
  };

  const mapToFailedItem = (ps: any): FailedItemNormalized => {
    // Prefer imageBag, fallback to images.sources[0].main if present
    const image = ps?.imageBag || ps?.images?.sources?.[0]?.main || '';
    return {
      id: ps?.id || ps?.code || '',
      name: ps?.name || ps?.codeDescription || '',
      image,
    };
  };

  const removeSucceededFromBag = async ({
    bagID,
    localeParam,
    marketParam,
    voucher,
    succeededProducts,
  }: {
    bagID: string;
    localeParam: string;
    marketParam: string;
    voucher: string;
    succeededProducts: Array<{ sku: string; size: string }>;
  }): Promise<void> => {
    const currentBag = await retrieveBag({
      context: undefined,
      payload: { hashOrderId: bagID, profile: { countryId: marketParam, languageId: getLang(localeParam) } },
    });

    const productsCurrentlyInBag = currentBag?.shoppingCart;
    if (!productsCurrentlyInBag?.length) return;

    let remainingSucceeded = [...succeededProducts];
    const newBag = [...productsCurrentlyInBag].filter((item) => {
      const successfulProduct = remainingSucceeded.find((s) => s.sku === item.productId && s.size === item.size);
      if (successfulProduct) {
        remainingSucceeded = remainingSucceeded.filter((s) => !(s.sku === successfulProduct.sku && s.size === successfulProduct.size));
        return false;
      }
      return true;
    });

    const employeeId = getCookie(COOKIES.EMPLOYEE_ID) || '';
    const zipCode = getCookie(COOKIES.ZIP) || '';
    const tax = zipCode && zipCode !== '' ? { zipcode: zipCode } : {};

    await getCart({
      lang: localeParam,
      products: newBag,
      hashOrderId: bagID,
      employeeId,
      bondValue: voucher,
      tax,
    });
  };

  const handleAddMultipleResult = async ({
    result,
    source,
    localeParam,
    marketParam,
  }: {
    result: any;
    source: FailureSource;
    localeParam: string;
    marketParam: string;
  }): Promise<{ succeededItems: any[]; failedOpened: boolean }> => {
    const failed = !result?.[0];
    const succeededProducts = result?.[1]?.filter((item: any) => item.status === 'OK') || [];

    if (failed) {
      const failedProducts = result?.[1]?.filter((item: any) => item.status !== 'OK') || [];
      const failedWithInfo: any[] = [];
      const getProductPromises: Promise<void>[] = [];

      failedProducts.forEach((failedItem: any) => {
        getProductPromises.push(
          getProduct({
            lang: localeParam,
            productCode: failedItem.sku,
            target: productSheet.realTarget || productSheet.target,
          }).then((productInfo: any) => {
            if (productInfo?.productSheet) {
              failedWithInfo.push(productInfo.productSheet);
            }
          }),
        );
      });

      await Promise.all(getProductPromises);

      // Cleanup succeeded items that may have been partially added
      if (succeededProducts.length > 0) {
        const bagID = getCookie(COOKIES.BAG);
        if (bagID) {
          const voucher = getCookie(COOKIES.BONDS) || '';
          await removeSucceededFromBag({
            bagID,
            localeParam,
            marketParam,
            voucher,
            succeededProducts: succeededProducts.map((s: any) => ({ sku: s.sku, size: s.size })),
          });
        }
      }

      // Normalize and open modal
      const normalized = failedWithInfo.map(mapToFailedItem);
      openFailureModal({ source, items: normalized });

      // ROKU-specific: refresh no-stock materials after failure
      if (source === 'roku' && selectedSize) {
        await handleGetRokuStock(selectedSize);
      }
    }

    // Update local bag state by appending succeeded items' shoppingCart payloads
    const updateForBag = [...bag, ...succeededProducts.map((item: any) => item.shoppingCart?.[0])];
    setBag(updateForBag);
    setBagLength(updateForBag.length);

    return { succeededItems: succeededProducts, failedOpened: failed };
  };

  const addToCartHandler = async ({ payload, cookiesBagId, showJustAdded }: any) => {
    const cookiesMarket = getCookie(COOKIES.BAG_MARKET);
    const cookiesShipment = getCookie(COOKIES.SHIPMENT);
    const cookiesBonds = getCookie(COOKIES.BONDS);
    const cookiesZip = getCookie(COOKIES.ZIP);
    const cookiesEmployeeId = getCookie(COOKIES.EMPLOYEE_ID);
    const cookiesPackstation = getCookie(COOKIES.PACKSTATION);
    const cookiesCtr = getCookie(COOKIES.IS_CTR);
    if (cookiesMarket !== market) {
      payload.hashOrderId = '';
      if (cookiesBagId) {
        eraseCookie(COOKIES.BAG);
        eraseCookie(COOKIES.BAG_DATE);
      }
      if (cookiesZip) {
        eraseCookie(COOKIES.ZIP);
      }
      if (cookiesShipment) {
        eraseCookie(COOKIES.SHIPMENT);
      }
      if (cookiesMarket !== null && cookiesBonds) {
        eraseCookie(COOKIES.BONDS, COOKIES.DOMAIN);
        eraseCookie(COOKIES.AUTO_BONDS);
      }
      if (cookiesEmployeeId) {
        eraseCookie(COOKIES.EMPLOYEE_ID);
      }
      if (cookiesPackstation) {
        eraseCookie(COOKIES.PACKSTATION);
      }
      if (cookiesCtr) {
        eraseCookie(COOKIES.IS_CTR);
      }
    }

    const [result, crossSellingProducts] = await Promise.all([addToBag({ ...payload }), getCrossSelling({ profile: locale, productId: payload.id })]);
    let updatedPromoLabel = null;
    let updatedPromoLabelType = null;
    const previousGridUrl = sessionStorage.getItem('gridUrl');

    if (productSheet?.baseUrl && previousGridUrl === undefined) {
      const [domain, fullUrl] = productSheet?.baseUrl?.split(locale);

      try {
        const splitUrl = fullUrl.split('/').filter(Boolean);
        const gridUrl = splitUrl.slice(0, splitUrl.length - 1).join('/');
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/${gridUrl}`);
      } catch (e) {
        // en caso de que algo fallase mandamos a la home
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/`);
      }
    }

    dataLayerCartClick({ ...payload, productSheet }, result, false, bag);
    if (result.hashOrderId) {
      setBagCookies({ hash: encodeURI(result.hashOrderId), market });

      const { sapActive, extendedBlockSAP, shoppingCart } = result;
      if (!sapActive && !extendedBlockSAP) {
        // can't pay
        window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
      } else {
        if (extendedBlockSAP) {
          // can pay
          setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
        }
        if (!showJustAdded) {
          window.location.assign(`${window.location.origin}/${locale}/bolsa`);
        }
        if (showJustAdded) {
          const { price } = shoppingCart[0]; // siempre es una array con un solo item
          obtainVoucherPrices({ ...shoppingCart[0], prices: price }, t, locale, contextFilters, activatedPrevouchers, false, true);
          const newBag = bag ? JSON.parse(JSON.stringify(bag)) : [];
          // handleStorage('setItem', 'justAddedItem', JSON.stringify({ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }));
          setBag(() => {
            const n = [{ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }, ...newBag];
            return n;
          });
          setBagLength(() => {
            const newLength = bagLength + 1;
            return newLength;
          });

          setRecommendedProductsForBag([...crossSellingProducts, ...recommendedProductsForBag].map((item, i) => ({ ...item, order: i })));

          if (showFreeShipping) {
            updatedPromoLabel = result?.freeShippingLabel || null;
          }
          // let promoLabelObtained = false;
          if (showGiftPurchase) {
            updatedPromoLabel = result?.gwplabel || result?.freeShippingLabel || null;
            if (result?.gwplabelObtained && result?.gwplabelObtained !== '') {
              updatedPromoLabel = result?.gwplabelObtained;
              // promoLabelObtained = true;
              updatedPromoLabelType = 'obtained';
            }
          }
          setLoading(false);
          setPromoLabel(updatedPromoLabel);
          setPromoLabelType(updatedPromoLabelType);
        }
      }
      // [TODO]: que quiten la cache de bolsa.json en integra
      // router.push('/bolsa');
    } else {
      setLoading(false);

      try {
        await debugAPI({ profile: locale, payload, response: result, url: window.location.href });
      } catch (e) {
        console.error(e);
      }
      // [TODO]: handle error
      // eslint-disable-next-line no-alert
      alert('No se puede procesar el pedido en este momento');
    }
  };

  const handleClickAddToCartBtn = async (showJustAdded = false, userIdentified = false) => {
    if (isBuyingDisabled) return;
    const isUserIdentified =
      getCookie(COOKIES.SELLIGENT_USRHASH) || getCookie(COOKIES.MEMBERS_ONLY_SUBSCRIBED) || userIdentified || bagHasMembersOnlyProduct || userData !== null;

    setCartError(false);

    if (!availableSizes.includes(selectedSize)) {
      const message = !isAccessory ? 'elige.talla' : 'producto.nodisponible';
      setCartError(t('ficha.producto', message));
      // check if window is less than 768px
      const mobileWindow = window.innerWidth < 768;
      if (!isAccessory && mobileWindow) {
        if (titleRef.current) {
          // scroll item to the top of the screen
          titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else if (getDomainValue({ nnormal: productSheet.kboix === true })) {
      // must select at least two soles to purchase kboix product
      // primero checkeamos que haya suelas suficientes en el array.
      const thumbnailDefault = productSheet.imageBag;
      const inspirationCode = productSheet.code;
      const kboixMin = productSheet.kboixData.minExtra;
      // la segunda parte del if es si es una suela sola en la pdp
      if (selectedMidsoles.length < kboixMin && productSheet.kboixData.midsoles.length > 0) {
        setCartError(t('kboix.soles', 'error.min', 'You must select at least two soles'));
        return;
      }
      try {
        setLoading(true);
        const mainId = productSheet.kboixData.sku;
        if (!mainId) {
          // no deberia de pasar nunca
          setCartError(t('kboix.soles', 'error.missing', 'Somthing went wrong'));
          return;
        }

        const midsolesForCart = selectedMidsoles.reduce((acc: any[], sole: any) => {
          const size = sole.sizes.find((size: any) => size.value === selectedSize);
          if (size.available) {
            const solePayload: { id: string; size: string; thumbnail: string; parent?: string } = {
              id: sole.id,
              size: selectedSize,
              thumbnail: thumbnailDefault.replace(inspirationCode, sole.id),
            };
            if (acc.length < kboixMin) {
              solePayload.parent = mainId;
            }
            acc.push(solePayload);
          }
          return acc;
        }, []);

        const productsToAdd = [{ id: mainId, size: selectedSize, thumbnail: thumbnailDefault.replace(inspirationCode, mainId) }, ...midsolesForCart];
        const { cartResult, crossResult }: any = await handleAddToBag({
          mainId: mainId,
          payload: { products: productsToAdd },
          allMandatory: true,
          checkStock: true,
          locale,
        });
        if (!isMounted()) return;
        //TODO: todo esto siguiente podemos cuando empezemos a refactorizar el carrito (mover a un contexto especifico)
        let updatedPromoLabel = null;
        let updatedPromoLabelType = null;

        if (cartResult.hashOrderId) {
          const addedIds = productsToAdd.map((item) => item.id);
          dataLayerCartClickV2({ data: cartResult.shoppingCart, addedIds: addedIds as any, bagId: cartResult.hashOrderId, currency: profileData?.currencySap });
          setBagCookies({ hash: encodeURI(cartResult.hashOrderId), market });

          const { sapActive, extendedBlockSAP, shoppingCart } = cartResult;
          if (!sapActive && !extendedBlockSAP) {
            // can't pay
            window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
            return;
          }
          if (extendedBlockSAP) {
            // can pay
            setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
          }
          if (!showJustAdded) {
            window.location.assign(`${window.location.origin}/${locale}/bolsa`);
            return;
          } else {
            const price = shoppingCart.find((cartItem: any) => cartItem.productId === productSheet.code)?.price;
            if (price) {
              obtainVoucherPrices({ ...shoppingCart[0], prices: price }, t, locale, contextFilters, activatedPrevouchers, false, true);
            }
            setBag(shoppingCart);
            setBagLength(shoppingCart.length);

            setRecommendedProductsForBag([...crossResult, ...recommendedProductsForBag].map((item, i) => ({ ...item, order: i })));

            if (showFreeShipping) {
              updatedPromoLabel = cartResult?.freeShippingLabel || null;
            }
            // let promoLabelObtained = false;
            if (showGiftPurchase) {
              updatedPromoLabel = cartResult?.gwplabel || cartResult?.freeShippingLabel || null;
              if (cartResult?.gwplabelObtained && cartResult?.gwplabelObtained !== '') {
                updatedPromoLabel = cartResult?.gwplabelObtained;
                // promoLabelObtained = true;
                updatedPromoLabelType = 'obtained';
              }
            }
            setLoading(false);
            setPromoLabel(updatedPromoLabel);
            setPromoLabelType(updatedPromoLabelType);
          }
        }
      } catch (error: any) {
        //TODO: handle Error;
        if (!isMounted()) return;
        setCartError(error?.message || 'No se puede procesar el pedido en este momento');
      }
    } else if (bagLength >= MAX_BAG_LENGTH) {
      setCartError(
        t('ficha.producto', 'alcanzado.limite.bolsa', 'The product could not be added. You have reached the limit of products in your shopping cart.'),
      );
    } else if (isMembersOnly && !isUserIdentified) {
      window.showMembersOnlyModal?.('pdp_members_only_popup', undefined, undefined, async () => {
        await handleClickAddToCartBtn(true, true);
      });
    } else if (hasSunneiConfig) {
      /**
       * Añadir a la bolsa en sunnei.
       * Primero necesitamos checkear que para la talla elegida tengamos stock de los dos productos. Si no hay stock de alguno, necesitamos bloquear la compra con un modal. Esto difiere de Roku en que lo hacemos antes de intentar añadir a la bolsa.
       * Ahora los Sunnei se compran en pares, por lo que necesitamos añadir el primero y luego el segundo.
       * Si falla el primero, no añadimos el segundo.
       * Si falla el segundo, quitamos el primero.
       */
      const sunneisWithoutStock: any[] = [];
      if (!firstProductHasStock) {
        sunneisWithoutStock.push((sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[0]]);
      }
      if (!secondProductHasStock) {
        sunneisWithoutStock.push((sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[1]]);
      }
      if (sunneisWithoutStock.length > 0) {
        openFailureModal({ source: 'sunnei', items: sunneisWithoutStock.map(mapToFailedItem) });
        return;
      }

      setLoading(showJustAdded);
      // separacion
      const addMultiplePayload = [
        {
          sku: (selectedSunneiShoes as any)[0],
          size: selectedSize,
          sizeLabel: selectedSize,
          price: (sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[0]]?.prices,
          gender: (sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[0]]?.targetId,
          checkStock: false,
        },
        {
          sku: (selectedSunneiShoes as any)[1],
          size: selectedSize,
          sizeLabel: selectedSize,
          price: (sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[1]]?.prices,
          gender: (sunneiInfo as any)?.products?.[(selectedSunneiShoes as any)[1]]?.targetId,
          checkStock: false,
        },
      ];
      const addToCartMultipleResult = await window.addToCartMultiple?.(addMultiplePayload, true, bag);
      try {
        await handleAddMultipleResult({ result: addToCartMultipleResult, source: 'sunnei', localeParam: locale, marketParam: market });
      } catch (e) {
        console.error(e);
      }

      setLoading(false);
    } else {
      const cookiesBagId = getCookie(COOKIES.BAG);
      setLoading(showJustAdded);
      const sizeLabel = !isAccessory ? sizes.filter((size: any) => size?.value === selectedSize)[0]?.label : selectedSize;
      const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
      const voucher = getCookie(COOKIES.BONDS) || '';
      const voucherDiscount = autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
      let pricesPayload = prices;
      const discountStrToNumber = prices?.discountStr ? Number(prices?.discountStr?.replace(/\D/g, '')) : 0;
      if (discountStrToNumber > prices.discount) {
        // tenemos que enviar el discount que mas beneficie
        pricesPayload.discount = discountStrToNumber;
      }

      if (prices.pricePerSize) {
        const sizeFound = selectedSize ? sizes.find((size: any) => size?.value === selectedSize) : null;

        if (sizeFound) {
          pricesPayload = sizeFound?.price;
        }
      }

      const payload: {
        id: string;
        price: object;
        profile: object;
        size: string;
        sizeLabel: string;
        gender: string;
        thumbnail: string;
        voucher?: string;
        hashOrderId?: string;
        camperone?: { jsonConfig: any };
        image?: string;
      } = {
        id: code,
        price: pricesPayload,
        profile: { countryId: getMarket(locale), languageId: getLang(locale) },
        size: selectedSize,
        sizeLabel,
        gender: productSheet.realTarget || productSheet.target || '',
        thumbnail: productSheet.imageBag || '', // en ocasiones queda como null y causa un error
      };
      if (voucherDiscount || voucher) {
        payload.voucher = voucher;
      }
      if (cookiesBagId) {
        payload.hashOrderId = decodeURI(cookiesBagId);
      }

      let camperOneJSON = null;
      if (isCamperOne || isRoku) {
        if (window.camperOneGetJson) {
          // old version camper one no wait
          try {
            camperOneJSON = window.camperOneGetJson();
          } catch (e) {
            // failed because jsonBO is undefined since
            // user comes from mobile and never customized (didn't load plugin)
            camperOneJSON = productSheet.camperone.jsonConfig;
          }

          payload.camperone = { jsonConfig: camperOneJSON };

          addToCartHandler({ payload, cookiesBagId, showJustAdded });
        } else if (window.customDIM?.api) {
          // new dim version, wait for getJSON to finish
          try {
            window.customDIM.api.getCaptureAndJSON(
              512,
              512,
              'ffffff',
              async (data: any) => {
                const somePieceWithoutMaterial = data.piezas.some((item: any) => {
                  if (!['BORT', 'BORC', 'BOLT', 'BOLC'].includes(item?.idPieza)) {
                    return item?.material?.length === 0;
                  }

                  return false;
                });

                if (somePieceWithoutMaterial) {
                  // mostramos error rojo en plan talla
                  setCartError(t('ficha.producto', 'customize.required.pieces'));
                  setLoading(false);
                  return false;
                } else {
                  const viewUrl = data?.urlVistas;
                  const base64Image = data?.image;

                  data.urlVistas = [viewUrl];

                  if (base64Image) {
                    payload.image = base64Image;
                  }

                  delete data.image;

                  if (isRoku) {
                    const { piezas } = data;
                    const addMultiplePayload = piezas
                      .filter((pieza: any) => pieza?.customizable === true)
                      .map((item: any) => ({ sku: item.material[0].idMaterialEshop, size: selectedSize }));

                    const addToCartMultipleResult = await window.addToCartMultiple?.(addMultiplePayload, true, bag);
                    try {
                      await handleAddMultipleResult({ result: addToCartMultipleResult, source: 'roku', localeParam: locale, marketParam: market });
                    } catch (e) {
                      console.error(e);
                    }
                    setLoading(false);
                  } else {
                    payload.camperone = { jsonConfig: data };

                    addToCartHandler({ payload, cookiesBagId, showJustAdded });
                  }
                }
              },
              (error: any) => {
                console.error('error', error);
                throw error;
              },
            );
          } catch (e) {
            console.error('failed to get json from dim');
            console.error(e);
            camperOneJSON = productSheet.camperone.jsonConfig;
            payload.camperone = { jsonConfig: camperOneJSON };
            addToCartHandler({ payload, cookiesBagId, showJustAdded });
          }
        }
      } else {
        // no wait
        addToCartHandler({ payload, cookiesBagId, showJustAdded });
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    });
    const urlParams = new URLSearchParams(window.location.search);
    const sizeValue = urlParams.get('size');
    const sizeIsAvailable = sizes && sizes.filter((size: any) => size.value === sizeValue && (size.available || notifyMe)).length > 0;
    if (sizeValue && sizeIsAvailable) {
      setSelectedSize(sizeValue);
      setSunneiSize(sizeValue);
    } else {
      setSelectedSize(defaultSelectedSize);
      setSunneiSize(defaultSelectedSize);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let sizeFound: any = selectedSize ? sizes.find((size: any) => size?.value === selectedSize) : {};
  if (!sizeFound) {
    sizeFound = {};
  }

  const isPresale = productSheet.presales;

  let addToCartLabel = !isPresale ? t('ficha.producto', 'add.to.bag', 'Add to bag') : t('ficha.producto', 'add.to.bag.presale', 'PRE-ORDER');

  if (isMembersOnly) {
    addToCartLabel = `${!isPresale ? t('ficha.producto', 'members.only.shop') : t('ficha.producto', 'add.to.bag.presale')} ${t('ficha.producto', 'members.only', '(Members Only)')}`;
  }

  const priceRange =
    prices?.priceDiscountFromFormatted && prices?.priceDiscountToFormatted ?
      `${prices.priceDiscountFromFormatted} - ${prices.priceDiscountToFormatted}`
    : `${prices?.priceFromFormatted} - ${prices?.priceToFormatted}`;

  const notifymeBySize = notifyMe && productSheet?.notifyMeType === 'SIZE';

  let shouldShowSizes = availablePrice;
  const hasSizeGuide = Array.isArray(productSheet.sizesGuide) && productSheet.sizesGuide.length > 0 && !storeData;
  let shouldShowButton =
    availablePrice &&
    (!productSheet.notifyMe || (productSheet.notifyMe && notifymeBySize && (availableSizes.includes(selectedSize) || selectedSize === undefined)));
  // si el producto tiene talla unica, es notifyMe por talla y no tiene disponibilidad, no debería mostrar nada
  if (productSheet.notifyMe && productSheet.notifyMeType === 'SIZE' && sizes.length === 1 && sizes.every((size: any) => !size.available)) {
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  if (availableSizes.length === 0 && !productSheet?.notifyMe) {
    // NO es notifyme y no tiene disponibilidad de tallas, debería ser como si no tuviese precio
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  const sizeGuideInfo = SIZE_GUIDE_WITH_MODELS.includes(productSheet.code) && productSheet.description ? productSheet.description : null;
  const isStandardNotifyMe = productSheet?.notifyMeType !== null && productSheet?.notifyMeType !== 'SIZE';
  const isBySizeWithNoSizes = !isStandardNotifyMe && availableSizes.length === 0;
  const hideFewStockMessage = isStandardNotifyMe || isBySizeWithNoSizes;
  const fixedPrice = getFixedPrice(market, profileData, prices?.current);

  useEffect(() => {
    try {
      if (skusWithoutStock !== null && isDimReady === true) {
        const emptyFunc = () => undefined;

        window.customDIM.api.setNoStockMaterials(skusWithoutStock, emptyFunc, emptyFunc);
      }
    } catch (e) {
      console.error('DIM error', e);
    }
  }, [skusWithoutStock, isDimReady]);

  const handleSelectRokuSize = async (size: any) => {
    setDefaultSelectedSize(size);
    await handleSelectSize(size);
    setShowSizeModal(false);
  };

  const countriesWithSizeSelector = ['US'];
  const manualYellowBox = Boolean(productSheet.textDiscountCollectionOld && !usrhashValue);
  const autoYellowBox = ![null, undefined, ''].includes(voucherPriceText);

  return (
    // <Col xs={columnSizes.xs} md={columnSizes.md} lg={columnSizes.lg}>
    <div className="relative flex h-full flex-col p-8 pt-2.5 lab:max-w-none lab:max-lg:px-2.5 md:mx-auto md:max-w-[260px] md:px-0 md:pt-8">
      {getDomainValue({
        camper:
          !isGiftCard ?
            <div className="mb-2.5 nnormal:hidden md:hidden">
              <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
              <ProductBreadcrumbs className="mt-3.5" variant="new" withCurrent={true} />
            </div>
          : null,
      })}
      <div className={`${styles.titleWrapper} max-md:mt-4 lab:max-md:mt-0`}>
        <>
          <h1 ref={titleRef} className={`scroll-m-14 ${styles.brandTitle} ${availablePrice ? '' : styles.brandTitleNotify}`}>
            {`${productSheet.brand} `}
            {productSheet.codeDescription ?
              <span
                dangerouslySetInnerHTML={{ __html: productSheet.codeDescription }}
                className={availablePrice || productSheet?.notifyMeType === 'OUT' ? styles.codeDescription : styles.codeDescriptionNotify}
              />
            : null}
          </h1>
          {getDomainValue({
            camper: null,
            camperlab: null,
            nnormal:
              productSheet?.codeFeatures ?
                <h2 dangerouslySetInnerHTML={{ __html: productSheet?.codeFeatures }} className={`${styles.codeDescription} ${styles.codeFeatures}`} />
              : null,
          })}
          {isGiftCard ?
            <div className="mt-[22px] max-md:hidden">
              <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
            </div>
          : null}
          <GiftCardTargetLinks className="mt-6" />
          {(appmode || storeData) && <span className={styles.productSku}>{productSheet.code}</span>}
          {isCamperOne && !isRoku && <ONESVG className={styles.camperONESVG} />}
          {!isGiftCard && productSheet?.notifyMeType !== 'OUT' && (
            <>
              {getDomainValue({ camper: recalculating && <Skeleton className="h-4 w-12 min-w-[30%]" />, camperlab: null, nnormal: null })}
              {!recalculating && (
                <p
                  className={availablePrice ? `${styles.productCurrentPrice} ${styles.priceSpan}` : styles.productCurrentPriceNotify}
                  data-ge-price="data-ge-price"
                  data-fp={fixedPrice}
                >
                  {/* es horrible y me sabe mal pero la lógica es funcional y es para ir rápido */}
                  {prices !== null && prices.pricePerSize ?
                    !availableSizes.includes(selectedSize) ?
                      priceRange
                    : sizeFound.price.currentFormated
                  : null}
                  {prices !== null && !prices.pricePerSize && productSheet.prices.currentFormated}
                </p>
              )}
              {!recalculating && availablePrice && productSheet.prices && (productSheet.prices.previous || productSheet.prices.priceDiscountFromFormatted) && (
                <p className={styles.discountPriceText}>
                  <del className={styles.discountPricePrevious} data-fp={getFixedPrice(market, profileData, productSheet?.prices?.previous)}>
                    {prices !== null && prices.pricePerSize ?
                      !availableSizes.includes(selectedSize) ?
                        `${prices.priceFromFormatted} - ${prices.priceToFormatted}`
                      : sizes.find((size: any) => size?.value === selectedSize)?.price?.previousFormated
                    : null}
                    {prices !== null && !prices.pricePerSize && productSheet.prices.previousFormated}
                  </del>
                  <span
                    className={`${getDomainValue({ camper: voucherApplied ? `${styles.discountFromVoucher}` : '', nnormal: '', camperlab: '' })} green`}
                  >{`${productSheet?.prices?.discountStr || `-${productSheet?.prices?.discount ?? productSheet?.prices?.discountLocal}%`}`}</span>
                </p>
              )}
              {!recalculating &&
                voucherApplied &&
                productSheet.prices?.previous !== undefined &&
                productSheet.prices?.previous > productSheet.prices?.current &&
                voucherPriceText === null && <p className={styles.voucherApplied}>{productSheet.prices.appliedLabel}</p>}
              {prices?.pricePerSize && <p className={styles.pricePerSize}>{t('listado.productos', 'final.price.acording', 'Final price acording to size')}</p>}
            </>
          )}
        </>
        {!isGiftCard ?
          <>
            {getDomainValue({
              camper: market === 'AU' && availablePrice && <AfterpayButton totalPrice={totalPrice} currency={prices.currency || 'AUD'} />,
              camperlab: null,
              nnormal: null,
            })}
            {!camperlab && BAZAAR_VOICE_MARKETS.includes(market) && (
              <div id="product_rating" className={styles.productRating}>
                <div data-bv-show="rating_summary" data-bv-product-id={productSheet?.code}></div>
              </div>
            )}
            {availablePrice && reviewsCount > 0 && !productSheet.recycled && !productSheet.camperlab && (
              <div className={styles.rateWrapper} onClick={onClickReviews}>
                <StarRate allowHalf value={averageRate} character={<RateSVG />} />
                <p className="relative -top-0.5 mb-3 ml-2.5 inline-block cursor-pointer text-xs font-normal underline">{`${reviewsCount} ${t('ficha.producto', 'see.reviews')}`}</p>
              </div>
            )}
          </>
        : <GiftCard prices={productSheet.prices} locale={locale} productSheet={productSheet} />}
      </div>
      {!isGiftCard ?
        <div className="mt-[22px] camper:max-md:hidden">
          <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
        </div>
      : null}
      {/* productSheet.textDiscountCollection &&
            <p className={recalculating ? `${styles.textDiscount} subclaimcta` : styles.textDiscount} dangerouslySetInnerHTML={{ __html: productSheet.textDiscountCollection }} />
          */}
      {getDomainValue({
        nnormal:
          hasMidsoles ?
            <KboixMidsoles
              midsoles={productSheet.kboixData.midsoles}
              selected={selectedMidsoles}
              setSelected={setSelectedMidsoles}
              selectedSize={selectedSize}
              canConfig={productSheet.kboixData.canConfig}
              min={productSheet.kboixData.minExtra}
            />
          : null,
      })}
      {productSheet?.notifyMeType !== 'OUT' && manualYellowBox && !autoYellowBox && (
        <p className={styles.textDiscount} dangerouslySetInnerHTML={{ __html: productSheet.textDiscountCollectionOld }} />
      )}
      {productSheet?.notifyMeType !== 'OUT' && !recalculating && autoYellowBox && (
        <p className={styles.textDiscount} dangerouslySetInnerHTML={{ __html: voucherPriceText }} />
      )}
      <TargetLinks className="mt-6" />
      <div id="row-sizes" className="flex flex-col md:flex-row lab:lg:gap-12 lab:xl:gap-32">
        <div className="basis-full">
          <div id="sizes">
            <div
              data-sizes={shouldShowSizes && !isUniqueSize}
              className="my-[22px] flex items-center justify-between data-[sizes=false]:mb-0 data-[sizes=false]:justify-end empty:hidden nnormal:my-4"
            >
              {/* En camperlab la han liado y metido todo con las tallas de US por eso hay que hacer la comprobación para que si es camperlab, apparel y pantalones mostrar (US) en vez del pais correspondiente */}
              {getDomainValue({
                camper:
                  shouldShowSizes && !isUniqueSize ?
                    <h3
                      className="mb-0 text-sm [@media(min-width:1440px)]:text-xs"
                      dangerouslySetInnerHTML={{
                        __html: `${t('ficha.producto', `elige.talla.${productSheet.realTarget}`, `***Choose your ${productSheet.realTarget} size`)} (${productSheet.camperlab && productSheet.family === String(FAMILIES.CLOTHES) && ['trousers', 'shorts'].includes(productSheet.sizesGuide?.[0]?.productType) ? 'US' : country})`,
                      }}
                    />
                  : null,
              })}
              {hasSizeGuide ?
                <SizesGuide
                  target={productSheet.realTarget}
                  code={code}
                  name={productSheet?.brand?.toLowerCase()}
                  guide={productSheet.sizesGuide}
                  currentCountry={country}
                  sizeGuideInfo={sizeGuideInfo}
                />
              : null}
            </div>
          </div>
          {shouldShowSizes && !isUniqueSize && (
            <>
              <SizesList
                isCamperOne={isCamperOne}
                sizes={sizes}
                defaultSelectedSize={defaultSelectedSize}
                onChange={handleSelectSize}
                isSock={isSock}
                showNotifyMe
                locale={locale}
                code={code}
                select={getDomainValue({
                  camper: countriesWithSizeSelector.includes(market) && [FAMILIES.SHOES, FAMILIES.ACCESSORIES].includes(Number(productSheet.family)),
                  nnormal: true,
                  camperlab: countriesWithSizeSelector.includes(market) && [FAMILIES.SHOES, FAMILIES.ACCESSORIES].includes(Number(productSheet.family)),
                })}
                currentCountry={country}
                productSheet={productSheet}
                showLimitedSizeStyle={undefined}
                onMouseOverSize={undefined}
                onMouseOutSize={undefined}
                calculateColumns={undefined}
              />

              {
                getDomainValue({
                  camper: !isCamperOne && limitedSizes.includes(selectedSize) && !hideFewStockMessage && (
                    <p className={styles.limitedSizesLabel}>* {t('ficha.producto', 'size.info.only.few')}</p>
                  ),
                }) as ReactNode | null
              }

              {
                getDomainValue({
                  camper: !isCamperOne && limitedSizes?.length > 0 && (!availableSizes.includes(selectedSize) || !limitedSizes.includes(selectedSize)) && (
                    <p className={styles.limitedSizesLabel}>* {t('ficha.producto', 'size.info.few.standby')}</p>
                  ),
                }) as ReactNode | null
              }
            </>
          )}
        </div>
      </div>
      <div className={styles.stickyCol} id="sticky-col">
        {shouldShowButton && !isGiftCard ?
          <div className={styles.addToCartBtn} ref={ref}>
            {cartError && <p className={styles.cartErrorText}>{cartError}</p>}
            {isFullScreen === false && (
              <AddToBagBtn
                id="add-to-cart"
                selectedSize={selectedSize}
                disabled={isBuyingDisabled}
                loading={loading === true}
                onMouseOver={() => !isBuyingDisabled && setIsMouseOverCartBtn(true)}
                onMouseLeave={() => !isBuyingDisabled && setIsMouseOverCartBtn(false)}
                onClick={() => handleClickAddToCartBtn(true)}
                className={isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? styles.addToCartBtnDisabled : ''}
                label={
                  isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? t('ficha.producto', 'elije.tu.talla') : addToCartLabel
                }
                market={market}
                setLoading={setLoading}
                extraData={productSheet.extraData}
                voucherApplied={voucherApplied}
              />
            )}
          </div>
        : !isGiftCard && (
            <>
              {productSheet.notifyMe && !productSheet.recycled && availablePrice && (
                <div className={styles.notifyMe}>
                  {t(
                    'ficha.producto',
                    `avisame.texto.${productSheet.notifyMeType}`,
                    '***Este producto no esta a la venta en este momento, pero podemos avisarte por email en cuanto este disponible',
                  )}
                </div>
              )}
              {notifyMe && !shouldShowButton ?
                <NotifyMeButton defaultSize={selectedSize} code={code} productSheet={productSheet} camperlab={camperlab} />
              : <div className={styles.noStock}>{t('ficha.producto', 'sin.stock')}</div>}
            </>
          )
        }
        {shouldShowButton && !isGiftCard && (
          <div
            id="floating-add-to-cart"
            className={`invisible fixed bottom-0 left-0 z-[100] w-full bg-neutral opacity-0 transition-opacity duration-300 md:hidden lab:md:block lab:lg:hidden ${styles.addToCartBtnFixed} ${!inView ? `${styles.visible}` : ''}`}
          >
            {/* {cartError && <p className={styles.cartErrorText}>{cartError}</p>} */}
            {isFullScreen === false && (
              <AddToBagBtn
                id="floating-button"
                floatingButton
                selectedSize={selectedSize}
                disabled={isBuyingDisabled}
                loading={loading === true}
                onMouseOver={() => !isBuyingDisabled && setIsMouseOverCartBtn(true)}
                onMouseLeave={() => !isBuyingDisabled && setIsMouseOverCartBtn(false)}
                onClick={() => handleClickAddToCartBtn(true)}
                className={isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? styles.addToCartBtnDisabled : ''}
                label={!availableSizes.includes(selectedSize) || !availablePrice ? t('ficha.producto', 'elije.tu.talla') : addToCartLabel}
                market={market}
                setLoading={setLoading}
                extraData={productSheet.extraData}
                voucherApplied={voucherApplied}
              />
            )}
          </div>
        )}
        {/* {!IS_NNORMAL && !profileData.global_e && !appmode && !storeData && (
            <AddToWishlistBtn
              product={{
                productId: code,
                size: selectedSize || '',
                profile: {
                  countryId: getMarket(locale),
                  languageId: getLang(locale),
                },
              }}
              productSheet={productSheet}
              isCamperOne={isCamperOne}
            />
          )} */}
        {/* {IS_CAMPER ?
            <ProductShare baseUrl={productSheet.baseUrl} code={productSheet.code} image={productSheet.image} />
          : null} */}
        {getDomainValue({
          camper: (
            <div className="mt-3">
              <DispoModal productSheet={productSheet} />
            </div>
          ),
        })}
        {!availablePrice && reviewsCount > 0 && !productSheet.recycled && !productSheet.camperlab && (
          <div className={`${styles.rateWrapper} ${styles.rateWrapperNotify}`} onClick={onClickReviews}>
            <StarRate allowHalf value={averageRate} character={<RateSVG />} />
            <h3 className="relative -top-0.5 mb-3 ml-2.5 inline-block cursor-pointer text-xs font-normal underline">{`${reviewsCount} ${t('ficha.producto', 'see.reviews')}`}</h3>
          </div>
        )}
        <ProductInfoSection />
        <DialogRoot
          showOverlay
          isOpen={failureModal.isOpen}
          onOpenChange={() => {
            closeFailureModal();
          }}
          className={`${styles.rokuWarningModal}`}
        >
          <Dialog className="w-full md:w-[875px] md:min-w-[875px] md:max-w-[875px]">
            <div className={styles.modalText}>
              <h2>
                <strong>{t('ficha.producto', 'we.are.sorry', '***¡Lo sentimos!')}</strong>
              </h2>
              <h3>{t('ficha.producto', 'roku.failed.add.to.bag', '***Alguno de los productos no se han podido añadir a la bolsa.')}</h3>
              <div className="my-3 flex flex-wrap gap-2">
                {failureModal.items.map((failedItem) => (
                  <FailedItem key={`failed-piece-${failedItem.id}`} image={failedItem.image} name={failedItem.name} sku={failedItem.id} />
                ))}
              </div>
              <Button onPress={() => closeFailureModal()} variant="solid">
                {t('ficha.producto', 'seguir.customizando')}
              </Button>
            </div>
          </Dialog>
        </DialogRoot>
        <DialogRoot
          showOverlay
          isOpen={showSizeModal}
          onOpenChange={() => {
            setShowSizeModal(false);
          }}
          className={`${styles.sizesModal}`}
        >
          <Dialog className="w-full bg-dim md:w-[400px] md:min-w-[400px] md:max-w-[400px]">
            <DialogHeader />

            <h2>{t('ficha.producto', 'sizes.modal.title')}</h2>
            <div className={styles.sizeListModal}>
              <SizesList
                isCamperOne={isCamperOne}
                sizes={sizes}
                defaultSelectedSize={defaultSelectedSize}
                onChange={handleSelectRokuSize}
                isSock={isSock}
                showNotifyMe={false}
                locale={locale}
                code={code}
                select={false}
                currentCountry={country}
                productSheet={productSheet}
                showLimitedSizeStyle={undefined}
                onMouseOverSize={undefined}
                onMouseOutSize={undefined}
                calculateColumns={undefined}
              />
            </div>
          </Dialog>
        </DialogRoot>
      </div>
    </div>
  );
}
